import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Users, Mail, Hash, CheckCircle2, AlertTriangle, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import InviteDialog from "@/components/InviteDialog";

export default function MyClientsPage() {
  const { clients, activeClientId, setActiveClientId, refreshClients } = useAuth();
  const [form, setForm] = useState({ client_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => { refreshClients(); }, [refreshClients]);

  const submit = async () => {
    if (!form.client_email) { toast.error("Enter the client's email"); return; }
    setBusy(true);
    try {
      await api.post("/me/request-join", form);
      toast.success("Join request sent — the client will see it under Approvals");
      setForm({ client_email: "", message: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="my-clients-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-worker" /> My Clients
          </h1>
          <p className="text-muted-foreground">Every client you currently support. Switch between them at any time using the dropdown in the sidebar.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-client-btn"
                className="rounded-full font-bold gap-2 min-h-[48px]"
                style={{ background: "hsl(var(--worker))", color: "hsl(var(--worker-fg))" }}>
          <Mail className="w-4 h-4" /> Invite a client to the app
        </Button>
      </header>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        targetRole="client"
        testIdPrefix="invite-client"
        onSent={refreshClients}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        {clients.map((c) => {
          const isActive = (activeClientId || clients.find((x) => x.active)?.client_id) === c.client_id;
          return (
            <Card key={c.client_id} className={`rounded-3xl p-5 border-2 transition-all ${isActive ? "ring-2 ring-worker shadow-lg" : "card-lift"}`}
                  data-testid={`my-client-card-${c.client_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-display font-extrabold text-2xl truncate">{c.client_name || "—"}</h2>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    {c.client_email && <span className="rounded-full px-3 py-1 bg-muted font-semibold"><Mail className="w-3 h-3 inline mr-1" /> {c.client_email}</span>}
                    {c.ndis_number && <span className="rounded-full px-3 py-1 bg-client/10 text-client font-bold border-2 border-client/20"><Hash className="w-3 h-3 inline mr-1" /> NDIS {c.ndis_number}</span>}
                  </div>
                </div>
                {isActive
                  ? <span className="rounded-full px-3 py-1 bg-worker/15 text-worker text-xs font-bold uppercase tracking-wider whitespace-nowrap"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Active</span>
                  : <Button size="sm" className="rounded-full font-bold" style={{ background: "hsl(var(--worker))", color: "hsl(var(--worker-fg))" }}
                            onClick={() => setActiveClientId(c.client_id)}
                            data-testid={`switch-client-${c.client_id}`}>Switch</Button>}
              </div>
              {!c.is_subscriber && (
                <div className="mt-3 rounded-xl border-2 border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive font-bold flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> This client's subscription is inactive — you can view but not edit.
                </div>
              )}
            </Card>
          );
        })}
        {clients.length === 0 && (
          <Card className="rounded-3xl p-10 text-center text-muted-foreground border-2 col-span-2">No clients yet.</Card>
        )}
      </div>

      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="join-request-card">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2">
          <Send className="w-5 h-5 text-coral" /> Request to join another client
        </h2>
        <p className="text-sm text-muted-foreground">Enter the client's sign-in email. They'll see your request under <b>Approvals</b> and can accept or decline. Once accepted, they appear in your switcher above.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Client email</Label>
            <Input value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))}
                   placeholder="e.g. sarah@example.com" type="email"
                   className="rounded-xl min-h-[48px]" data-testid="join-email-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Short message (optional)</Label>
            <Textarea rows={2} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder="Hi! I'd love to join your support team — Jane referred me."
                      className="rounded-xl" data-testid="join-message-input" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy}
                  className="rounded-full font-bold gap-2 min-h-[48px]"
                  style={{ background: "hsl(var(--coral))", color: "white" }}
                  data-testid="send-join-request-btn">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send request"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
